import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function AllAppointments() {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    // Check for auth and load user
    useEffect(() => {
        const token = localStorage.getItem('emba_access');
        const userData = localStorage.getItem('emba_user');

        if (!token) {
            navigate('/login');
            return;
        }

        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                const actualUser = parsedUser.user || parsedUser;
                setUser(actualUser);

                if (actualUser.role !== 'ADMIN') {
                    navigate('/'); // Redirect non-admins
                }
            } catch (e) {
                console.error("Failed to parse user data", e);
                navigate('/login');
            }
        }
    }, [navigate]);

    // Fetch all appointments
    useEffect(() => {
        if (!user || user.role !== 'ADMIN') return;

        const fetchAppointments = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('emba_access');
                // Assuming this endpoint exists for admins to get all appointments
                const res = await fetch('https://emba-api.uat.varifica.com/api/appointments/', {
                    method: 'GET',
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await res.json().catch(() => ({}));

                if (!res.ok) {
                    throw new Error(data?.detail || data?.message || 'Failed to fetch appointments');
                }

                // Handle paginated response
                const apps = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
                setAppointments(apps);
                setFilteredAppointments(apps);
            } catch (err) {
                console.error(err);
                setError('Could not load appointments. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchAppointments();
    }, [user]);

    // Filter logic
    useEffect(() => {
        let result = appointments;

        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(app =>
                (app.id && app.id.toLowerCase().includes(lowerTerm)) ||
                (app.appointment_user?.first_name && app.appointment_user.first_name.toLowerCase().includes(lowerTerm)) ||
                (app.appointment_user?.last_name && app.appointment_user.last_name.toLowerCase().includes(lowerTerm)) ||
                (app.appointment_user?.email && app.appointment_user.email.toLowerCase().includes(lowerTerm)) ||
                (app.appointment_user?.phone_number && app.appointment_user.phone_number.includes(searchTerm))
            );
        }

        if (dateFilter) {
            result = result.filter(app => app.appointment_date === dateFilter);
        }

        setFilteredAppointments(result);
    }, [searchTerm, dateFilter, appointments]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(date);
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const [h, m] = timeStr.split(':');
        const date = new Date();
        date.setHours(parseInt(h, 10));
        date.setMinutes(parseInt(m, 10));
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        }).format(date);
    };

    return (
        <div className="min-h-screen bg-neutral-50 text-neutral-900 font-sans">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
                        EMBA by Emefa
                    </Link>

                    <div className="flex items-center gap-6">
                        {user && (
                            <span className="text-[10px] tracking-widest uppercase font-medium text-gray-500 hidden md:block">
                                Admin: {user.first_name}
                            </span>
                        )}
                        <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
                            <Link to="/" className="hover:text-black transition-colors">Home</Link>
                            <Link to="/profile" className="hover:text-black transition-colors">Account</Link>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="pt-32 pb-20 px-4 md:px-6 max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">All Appointments</h1>
                        <p className="text-sm text-neutral-600">Manage all system bookings.</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-8 flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Search</label>
                        <input
                            type="text"
                            placeholder="Search by name, email, phone or ID..."
                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Date</label>
                        <input
                            type="date"
                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => { setSearchTerm(''); setDateFilter(''); }}
                            className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500 hover:text-black border border-transparent hover:border-gray-200 rounded-lg transition-all"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900"></div>
                    </div>
                ) : error ? (
                    <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-sm text-red-600 text-center">
                        {error}
                    </div>
                ) : filteredAppointments.length === 0 ? (
                    <div className="text-center py-24 border border-dashed border-neutral-200 rounded-2xl bg-white">
                        <p className="text-neutral-400 text-sm">No appointments found matching your criteria.</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredAppointments.map((booking) => (
                            <motion.div
                                key={booking.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="group relative overflow-hidden rounded-xl border border-neutral-200 bg-white p-6 shadow-sm transition-all hover:shadow-md"
                            >
                                <div className="flex flex-col md:flex-row justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${booking.paid ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                                                {booking.paid ? 'Confirmed' : 'Pending Payment'}
                                            </span>
                                            <span className="text-[10px] text-neutral-400 uppercase tracking-wider">
                                                ID: {booking.id.slice(0, 8)}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Customer</p>
                                                <h3 className="font-medium text-neutral-900">
                                                    {booking.appointment_user ? `${booking.appointment_user.first_name} ${booking.appointment_user.last_name}` : 'Unknown User'}
                                                </h3>
                                                <p className="text-xs text-gray-500">{booking.appointment_user?.email || 'No email'}</p>
                                                <p className="text-xs text-gray-500">{booking.appointment_user?.phone_number || 'No phone'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Appointment</p>
                                                <h3 className="font-medium text-neutral-900">
                                                    {formatDate(booking.appointment_date)}
                                                </h3>
                                                <p className="text-neutral-500 text-sm flex items-center gap-2">
                                                    {formatTime(booking.appointment_time)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col justify-center items-end border-t md:border-t-0 md:border-l border-neutral-100 pt-4 md:pt-0 md:pl-8 min-w-[120px]">
                                        <p className="text-[10px] uppercase tracking-widest text-neutral-400">Amount</p>
                                        <p className="font-medium text-lg">GHS {booking.amount}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
